import { useEffect, useMemo, useState } from 'react';
import type { Appearance } from '@stripe/stripe-js';

/**
 * Stripe Payment Element styling via the Appearance API.
 * @see https://docs.stripe.com/elements/appearance-api
 */
export function getStripeElementsAppearance(isDark: boolean): Appearance {
  return {
    theme: isDark ? 'night' : 'stripe',
    variables: {
      borderRadius: '8px',
      colorPrimary: '#2563eb',
      fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif',
    },
  };
}

/**
 * Syncs with `html.dark` (class-based theme) so toggling light/dark updates embedded Stripe UI.
 */
export function useStripeElementsAppearance(): { appearance: Appearance; isDark: boolean } {
  const [isDark, setIsDark] = useState(() =>
    typeof document !== 'undefined' ? document.documentElement.classList.contains('dark') : false
  );

  useEffect(() => {
    const el = document.documentElement;
    const sync = () => setIsDark(el.classList.contains('dark'));
    const observer = new window.MutationObserver(sync);
    observer.observe(el, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  const appearance = useMemo(() => getStripeElementsAppearance(isDark), [isDark]);

  return { appearance, isDark };
}
